import React, { useState, useEffect } from "react";
import { Container, Col, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Field, reduxForm, InjectedFormProps } from "redux-form";
import { connect, useDispatch, useSelector } from "react-redux";
import axios from "axios";
import signup_banner from "../../../images/fx-rates.png";
import Footer from "../../Includes/Footer";
import BackButton from "../../Includes/BackButtonEntityAccount";
import renderField from "../../renderField";
import { RootState } from "../../store";
import { setPassword, setUserID, setwizardPageNumber } from "../../EntityAccountReducer";
import config from "../../../config";
import { ToastBox, notifyError } from "MyComponents/Toaster";

interface FormData {
  password: string;
  userId?: string;
}

interface SetPasswordProps {}

const SetPassword: React.FC<SetPasswordProps & InjectedFormProps<FormData, SetPasswordProps>> = ({ handleSubmit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const email = useSelector((state: RootState) => state.entityAccount.email);
  const businessName = useSelector((state: RootState) => state.entityAccount.businessName);
  const page = useSelector((state: RootState) => state.entityAccount.wizardPageNumber);

  const [passwordValidationError, setPasswordValidationError] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [eyeStatus, setEyeStatus] = useState(true);
  const [eyeStatusConfirmPassword, setEyeStatusConfirmPassword] = useState(true);
  const [password, setPasswordValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [validations, setValidations] = useState({
    minLength: false,
    hasNumber: false,
    hasUppercase: false,
    hasLowercase: false,
    hasSpecialCharacter: false,
    noSpaces: false,
  });

  const registermini_baseURL = `${config.NODE_API_BASE_URL}/wso2/registermini`;
  const theme = useSelector((state: RootState) => state.theme.theme);

  const handleTogglePassword = () => setShowPassword(!showPassword);
  const handleToggleConfirmedPassword = () => setShowConfirmPassword(!showConfirmPassword);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "password") {
      setPasswordValue(value);
      setValidations({
        minLength: value.length >= 8,
        hasNumber: /\d/.test(value),
        hasUppercase: /[A-Z]/.test(value),
        hasLowercase: /[a-z]/.test(value),
        hasSpecialCharacter: /[!@#$%^&*(),.?":{}|<>]/.test(value),
        noSpaces: !/\s/.test(value),
      });
      setPasswordValidation(false);
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
    }
  };

  const isFormValid: boolean =
    !password ||
    Object.values(validations).some((value: boolean) => !value) ||
    confirmPassword !== password;

  useEffect(() => {
    const hasValidationError = Object.values(validations).some((valid) => !valid);
    setPasswordValidationError(hasValidationError);
  }, [validations]);

  const validatePassword = (value: string) => {
    if (!value) {
      setEyeStatus(false);
      return t('common.password_required');
    }
    setEyeStatus(true);
    return undefined;
  };

  const submitForm = async (formValues: FormData) => {
    if (passwordValidationError) {
      setPasswordValidation(true);
      notifyError(t('common.invalid_password'));
      return;
    }
    const response = await sendOtpAndRegister(email, businessName, formValues.password, "entity");
    dispatch(setUserID(response.userId));
    dispatch(setPassword(formValues.password));
    dispatch(setwizardPageNumber(page + 1));
  };

  const sendOtpAndRegister = async (email: string, businessName: string, password: string, account_type: string) => {
    const miniRegister = await axios.post(registermini_baseURL, { email, firstName: businessName, password, account_type });
    return miniRegister.data;
  };

  const validateConfirmPassword = (value: string, allValues: FormData) => {
    if (!value) {
      setEyeStatusConfirmPassword(false);
      return t('common.confirm_password_required');
    }
    if (value !== allValues.password) {
      setEyeStatusConfirmPassword(true);
      return t('common.passwords_no_match');
    }
    setEyeStatusConfirmPassword(true);
    return undefined;
  };

  return (
    <>
      <ToastBox />
      <Container fluid className="px-0 flex-grow-1 d-flex flex-column">
        <div className="row g-0 flex-grow-1">
          <Col md={5} className="px-3 px-md-5 py-4">
            <p className="text-primary fw-medium fs-5 mt-3">{t('common.set_password')}</p>
            <Form noValidate onSubmit={handleSubmit(submitForm)}>
              <label htmlFor="password" className="form-label">{t('common.password')}</label>
              <div className="input-group password_field">
                <Field
                  type={showPassword ? "text" : "password"}
                  component={renderField}
                  className="form-control rounded-5 py-2 mt-2"
                  placeholder={t('common.enter_password')}
                  id="password"
                  name="password"
                  validate={validatePassword}
                  required
                  onChange={handleChange}
                />
                {eyeStatus && !passwordValidation && (
                  <span className="bg-transparent border-0 icon p-eye px-2" onClick={handleTogglePassword}>
                    <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                  </span>
                )}
              </div>
              <label htmlFor="confirmPassword" className="form-label mt-4">{t('common.confirm_password')}</label>
              <div className="input-group password_field">
                <Field
                  type={showConfirmPassword ? "text" : "password"}
                  component={renderField}
                  className="form-control rounded-5 py-2 mt-2"
                  placeholder={t('common.confirm_password_ph')}
                  id="confirmPassword"
                  name="confirmPassword"
                  validate={validateConfirmPassword}
                  required
                  onChange={handleChange}
                />
                {eyeStatusConfirmPassword && !passwordValidation && (
                  <span className="bg-transparent border-0 icon p-eye px-2" onClick={handleToggleConfirmedPassword}>
                    <i className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                  </span>
                )}
              </div>
 
              <PasswordValidation validations={validations} theme={theme} />
              <div className="d-grid gap-2 col-md-6 mt-4">
                <Button className="btn btn-primary mt-3 rounded-5" type="submit" disabled={isFormValid}>
                  {t('common.next')}
                </Button>
              </div>
            </Form>
          </Col>
          <Col
            md={7}
            className={`d-none d-md-flex flex-column align-items-center justify-content-center text-center py-5 ${theme === "light" ? "bg-sky" : "bg-dark-solid shadow-sm"}`}
          >
            <img src={signup_banner} className="img-fluid" style={{ maxWidth: 500, width: "100%" }} alt="banner" />
            <h2 className="mt-4">{t('common.all_in_one')}</h2>
            <p className="text-secondary">{t('common.all_in_one_desc')}</p>
          </Col>
        </div>
      </Container>
    </>
  );
};

const PasswordValidation: React.FC<{ validations: Record<string, boolean>; theme: any }> = ({ validations, theme }) => {
  const { t } = useTranslation();
  return (
    <div className={`mt-3 col-md-11 ${theme === "light" ? "bg-sky" : "bg-dark-solid shadow-sm"} rounded-4`}>
      <Form.Text className="text-muted">
        <div className="px-3 py-3 pb-3">
          <ValidationRow isValid={validations.minLength} label={t('common.pass_min_length')} />
          <ValidationRow isValid={validations.hasNumber} label={t('common.pass_has_number')} />
          <ValidationRow isValid={validations.hasUppercase} label={t('common.pass_has_upper')} />
          <ValidationRow isValid={validations.hasLowercase} label={t('common.pass_has_lower')} />
          <ValidationRow isValid={validations.hasSpecialCharacter} label={t('common.pass_has_special')} />
          <ValidationRow isValid={validations.noSpaces} label={t('common.pass_no_spaces')} />
        </div>
      </Form.Text>
    </div>
  );
};

const ValidationRow: React.FC<{ isValid: boolean; label: string }> = ({ isValid, label }) => {
  return (
    <span className="text-secondary py-2">
      <i className={`fa-solid ${isValid ? "fa-circle-check text-success" : "fa-circle-xmark text-danger"}`}></i>
      &nbsp;&nbsp;&nbsp;&nbsp;{label}
      <br />
    </span>
  );
};

const mapStateToProps = (state: RootState) => ({
  initialValues: { password: "" },
});

const form = reduxForm<FormData, SetPasswordProps>({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  form: "wizard",
  enableReinitialize: true,
})(SetPassword);

export default connect(mapStateToProps)(form);
